/* ============================================================
   CONFIGURATOR CORE JS
   Shared runtime for all LEVA configurators.

   Namespace:  window.Configurator
     .tokens      numeric/string design tokens mirrored from CSS
     .units       metric/imperial conversion + formatting
     .log         debug overlay (replaces CP_DEBUG)
     .bus         pub/sub event bus
     .icon(name)  SVG icon registry
     .UI          Sidebar / Drawer / Modal / Toast components
     .budget      BoQ publish/subscribe contract
     .version     current core version
   ============================================================ */

(function (global) {
  'use strict';

  if (global.Configurator && global.Configurator.__sealed) {
    console.warn('[Configurator] core already loaded; ignoring duplicate include');
    return;
  }

  var VERSION = 'core-v14-bppkg';

  /* ----------------------------------------------------------
     TOKENS  (JS-side mirror of select CSS vars)
     For values needed in JS logic (durations, breakpoint).
     ---------------------------------------------------------- */
  var tokens = {
    breakpointMobile: 768,
    durFast: 150,
    durBase: 250,
    durSlow: 400,
    color: {
      primary:  '#D2A878',
      action:   '#D8232A',
      text:     '#1A1C1D',
      bg:       '#F5F5F5',
      surface:  '#FFFFFF',
      success:  '#6EC96E',
      warn:     '#FFCC6E',
      error:    '#FF6E6E'
    }
  };

  /* ----------------------------------------------------------
     EVENT BUS  (Configurator.bus)
     ---------------------------------------------------------- */
  var listeners = {};
  var bus = {
    on: function (event, fn) {
      (listeners[event] = listeners[event] || []).push(fn);
      return function off() { bus.off(event, fn); };
    },
    off: function (event, fn) {
      var arr = listeners[event];
      if (!arr) return;
      var i = arr.indexOf(fn);
      if (i !== -1) arr.splice(i, 1);
    },
    emit: function (event, payload) {
      var arr = listeners[event];
      if (!arr) return;
      arr.slice().forEach(function (fn) {
        try { fn(payload); }
        catch (e) {
          try { log.error('[bus] listener for "' + event + '" threw: ' + e.message); }
          catch (_) {}
        }
      });
    }
  };

  /* ----------------------------------------------------------
     UNITS  (Configurator.units)
     Single source of truth for metric/imperial.
     mode is read-write; UI components read it through getMode().
     ---------------------------------------------------------- */
  var unitMode = 'metric';
  var units = {
    M_TO_FT:    3.28084,
    M2_TO_FT2:  10.7639,
    M3_TO_FT3:  35.3147,
    MM_TO_IN:   0.0393701,

    getMode: function () { return unitMode; },
    setMode: function (mode) {
      /* Accepts:
         - 'metric' / 'imperial'  (explicit)
         - true (legacy: metric=true) / false (legacy: imperial)
         - 'auto'  → detect from Shopify.currency.active or cart currency:
                      USD → imperial, anything else → metric */
      var next;
      if (mode === 'auto') {
        next = units.detectFromCurrency();
      } else if (mode === true)  { next = 'metric'; }
      else if (mode === false) { next = 'imperial'; }
      else { next = (mode === 'imperial') ? 'imperial' : 'metric'; }
      if (next === unitMode) return;
      unitMode = next;
      bus.emit('units:change', unitMode);
    },
    isMetric: function () { return unitMode === 'metric'; },

    /* Detect default unit mode from Shopify currency context.
       Returns 'metric' or 'imperial'. USD → imperial. */
    detectFromCurrency: function () {
      try {
        if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
          return Shopify.currency.active === 'USD' ? 'imperial' : 'metric';
        }
      } catch (e) {}
      return 'metric';
    },

    formatLength: function (m, decimals) {
      var d = (typeof decimals === 'number') ? decimals : 1;
      if (unitMode === 'metric') return m.toFixed(d) + ' m';
      return (m * 3.28084).toFixed(d) + "'";
    },
    formatArea: function (m2, decimals) {
      var d = (typeof decimals === 'number') ? decimals : 1;
      if (unitMode === 'metric') return m2.toFixed(d) + ' m²';
      return (m2 * 10.7639).toFixed(d) + ' ft²';
    },
    formatVolume: function (m3, decimals) {
      var d = (typeof decimals === 'number') ? decimals : 2;
      if (unitMode === 'metric') return m3.toFixed(d) + ' m³';
      return (m3 * 35.3147).toFixed(d) + ' ft³';
    },
    /* Convenience: convert any internal SI value to current display unit.
       returns { value, unit }. */
    displayLength: function (m) {
      if (unitMode === 'metric') return { value: m, unit: 'm' };
      return { value: m * 3.28084, unit: 'ft' };
    }
  };

  /* ----------------------------------------------------------
     LOG  (Configurator.log)
     Thin facade over Configurator.debug (loaded separately from
     configurator-debug.js). Mounts NO UI on its own.

     If configurator-debug.js is loaded AND debug is enabled,
     calls forward to the unified panel.
     If not loaded, calls are no-ops (silent).
     Either way, console is NOT patched here.
     ---------------------------------------------------------- */
  function dbg() { return global.Configurator && global.Configurator.debug; }

  function fmtArgs(args) {
    return Array.prototype.map.call(args, function (a) {
      if (a instanceof Error) {
        return a.message + (a.stack ? ('\n' + a.stack.split('\n').slice(0, 3).join('\n')) : '');
      }
      if (typeof a === 'object' && a !== null) {
        try { return JSON.stringify(a); } catch (e) { return String(a); }
      }
      return String(a);
    }).join(' ');
  }

  var log = {
    log:   function () { var d = dbg(); if (d) d.push('log',   fmtArgs(arguments)); },
    warn:  function () { var d = dbg(); if (d) d.push('warn',  fmtArgs(arguments)); },
    error: function () { var d = dbg(); if (d) d.push('error', fmtArgs(arguments)); },
    clear: function () { /* delegated; no local buffer */ },
    getEntries: function () { var d = dbg(); return d ? d.getLog() : []; },
    /* Modules register their presence checks here.
       Forwards to Configurator.debug.registerStatus if available;
       otherwise queues and replays once debug becomes available. */
    registerStatus: function (label, check, detail) {
      var d = dbg();
      if (d) { d.registerStatus(label, check, detail); return; }
      _pendingStatus.push([label, check, detail]);
    }
  };

  var _pendingStatus = [];
  function flushPending() {
    var d = dbg();
    if (!d || !_pendingStatus.length) return;
    _pendingStatus.forEach(function (s) { d.registerStatus(s[0], s[1], s[2]); });
    _pendingStatus.length = 0;
  }

  /* ----------------------------------------------------------
     ICON REGISTRY  (Configurator.icon)
     Icons used across all LEVA configurators (pool, sauna, etc).
     Extend via Configurator.registerIcon(name, svgMarkup).
     Hydrate DOM with: <span data-cfg-icon="name"></span>
     All icons render at 24x24, inherit currentColor.
     ---------------------------------------------------------- */
  var icons = {
    'pool-shape':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="2 10 8 10 8 20 16 20 16 14 22 14 22 4"/></svg>',
    'view-eye':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/>' +
      '<circle cx="12" cy="12" r="3"/></svg>',
    'stairs':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="3 20 8 20 8 15 13 15 13 10 18 10 18 5 21 5"/></svg>',
    'mirror':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M12 3 L12 21"/>' +
      '<path d="M4 8 L9 8 L9 16 L4 16 Z" stroke-dasharray="2 2"/>' +
      '<path d="M20 8 L15 8 L15 16 L20 16 Z"/></svg>',
    'rotate':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M3 12a9 9 0 1 0 3-6.7"/>' +
      '<polyline points="3 4 3 10 9 10"/></svg>',
    'lights':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="10" r="4"/>' +
      '<line x1="12" y1="2" x2="12" y2="4"/>' +
      '<line x1="12" y1="16" x2="12" y2="22"/>' +
      '<line x1="4" y1="10" x2="6" y2="10"/>' +
      '<line x1="18" y1="10" x2="20" y2="10"/>' +
      '<line x1="6.3" y1="4.3" x2="7.7" y2="5.7"/>' +
      '<line x1="16.3" y1="5.7" x2="17.7" y2="4.3"/></svg>',
    'led-pos':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="6" y="3" width="12" height="18" rx="1"/>' +
      '<line x1="6" y1="8" x2="18" y2="8"/>' +
      '<line x1="6" y1="14" x2="18" y2="14"/>' +
      '<circle cx="12" cy="5.5" r="1" fill="currentColor" stroke="none" class="cf-led-pos-dot cf-led-pos-top"/>' +
      '<circle cx="12" cy="11" r="1" fill="currentColor" stroke="none" class="cf-led-pos-dot cf-led-pos-middle" style="display:none"/>' +
      '<circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" class="cf-led-pos-dot cf-led-pos-bottom" style="display:none"/></svg>',
    'shaft':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="4" y="4" width="16" height="16" rx="1"/>' +
      '<line x1="4" y1="9" x2="20" y2="9"/>' +
      '<circle cx="9" cy="14" r="1.5"/>' +
      '<circle cx="15" cy="14" r="1.5"/>' +
      '<line x1="9" y1="17" x2="15" y2="17"/></svg>',
    'map':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z"/>' +
      '<path d="M9 4v16"/>' +
      '<path d="M15 6v16"/></svg>',
    'share':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="18" cy="5" r="3"/>' +
      '<circle cx="6" cy="12" r="3"/>' +
      '<circle cx="18" cy="19" r="3"/>' +
      '<line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/>' +
      '<line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/></svg>',
    'pdf':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
      '<polyline points="14 2 14 8 20 8"/>' +
      '<line x1="9" y1="15" x2="15" y2="15"/>' +
      '<line x1="9" y1="18" x2="13" y2="18"/></svg>',
    'save':
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8">' +
      '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>' +
      '<path d="M17 21v-8H7v8"/>' +
      '<path d="M7 3v5h8"/></svg>'
  };

  function icon(name, opts) {
    var raw = icons[name];
    if (!raw) return '';
    if (!opts) return raw;
    var size = opts.size || 24;
    var cls  = opts.className ? (' class="' + opts.className + '"') : '';
    return raw.replace('<svg ', '<svg width="' + size + '" height="' + size + '"' + cls + ' ');
  }
  function registerIcon(name, svg) { icons[name] = svg; }

  /* DOM hydration: replaces <span data-cfg-icon="X"> with SVG markup. */
  function hydrateIconsOne(el) {
    var name = el.getAttribute('data-cfg-icon');
    if (!name || el.getAttribute('data-cfg-icon-hydrated') === '1') return;
    var svg = icons[name];
    el.innerHTML = svg || '<!-- cfg-icon missing: ' + name + ' -->';
    el.setAttribute('data-cfg-icon-hydrated', '1');
  }
  function hydrateIcons(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-cfg-icon]:not([data-cfg-icon-hydrated])');
    for (var i = 0; i < nodes.length; i++) hydrateIconsOne(nodes[i]);
  }
  function observeIcons() {
    if (!global.MutationObserver) return;
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.hasAttribute && n.hasAttribute('data-cfg-icon')) hydrateIconsOne(n);
          if (n.querySelectorAll) hydrateIcons(n);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
  function bootIcons() {
    hydrateIcons(document);
    observeIcons();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootIcons);
  } else {
    bootIcons();
  }

  /* ----------------------------------------------------------
     UI COMPONENTS  (Configurator.UI)
     ---------------------------------------------------------- */
  var UI = {};

  /* --- UI.Sidebar ---------------------------------------------
     Vertical icon rail. mount(container, { side, items }).
     items: [{ key, icon, label, active?, onClick }]
     Returns { setActive, update, destroy }.
     ----------------------------------------------------------- */
  UI.Sidebar = {
    mount: function (container, opts) {
      var host = (typeof container === 'string')
        ? document.querySelector(container)
        : container;
      if (!host) { log.warn('[Sidebar] mount: container not found'); return null; }

      var side = (opts && opts.side === 'left') ? 'left' : 'right';
      var items = (opts && opts.items) || [];

      var root = document.createElement('div');
      root.className = 'cfg-sidebar cfg-sidebar--' + side;
      host.appendChild(root);

      var buttonsByKey = {};

      function render() {
        root.innerHTML = '';
        buttonsByKey = {};
        items.forEach(function (item) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'cfg-btn cfg-btn-icon' + (item.active ? ' is-active' : '');
          b.setAttribute('aria-label', item.label || item.key);
          b.title = item.label || item.key;
          b.innerHTML = icon(item.icon, { size: 22 });
          b.addEventListener('click', function (e) {
            if (typeof item.onClick === 'function') item.onClick(e, api);
          });
          root.appendChild(b);
          buttonsByKey[item.key] = b;
        });
      }

      var api = {
        setActive: function (key, isActive) {
          items.forEach(function (it) {
            if (it.key === key) it.active = !!isActive;
          });
          var b = buttonsByKey[key];
          if (b) b.classList.toggle('is-active', !!isActive);
        },
        update: function (nextItems) {
          items = nextItems || [];
          render();
        },
        destroy: function () {
          if (root.parentNode) root.parentNode.removeChild(root);
        },
        root: root
      };

      render();
      return api;
    }
  };

  /* --- UI.Drawer ----------------------------------------------
     Bottom sheet (mobile) / right card (desktop via @media).
     open({ title, contentHtml|contentNode, onClose }) → controller
     Only one drawer open at a time.
     ----------------------------------------------------------- */
  var activeDrawer = null;

  UI.Drawer = {
    open: function (opts) {
      opts = opts || {};
      if (activeDrawer) activeDrawer.close();

      var backdrop = document.createElement('div');
      backdrop.className = 'cfg-drawer-backdrop';

      var drawer = document.createElement('div');
      drawer.className = 'cfg-drawer';

      var inner = '<div class="cfg-drawer-handle"></div>';
      if (opts.title) {
        inner += '<h3 class="cfg-drawer-title">' +
          String(opts.title).replace(/</g, '&lt;') + '</h3>';
      }
      drawer.innerHTML = inner;

      var body = document.createElement('div');
      body.className = 'cfg-drawer-body';
      if (opts.contentNode) body.appendChild(opts.contentNode);
      else if (opts.contentHtml) body.innerHTML = opts.contentHtml;
      drawer.appendChild(body);

      document.body.appendChild(backdrop);
      document.body.appendChild(drawer);

      requestAnimationFrame(function () {
        backdrop.classList.add('is-open');
        drawer.classList.add('is-open');
      });

      function close() {
        backdrop.classList.remove('is-open');
        drawer.classList.remove('is-open');
        setTimeout(function () {
          if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
          if (drawer.parentNode)   drawer.parentNode.removeChild(drawer);
          if (typeof opts.onClose === 'function') opts.onClose();
          if (activeDrawer === ctrl) activeDrawer = null;
        }, tokens.durSlow);
      }
      backdrop.addEventListener('click', close);

      var ctrl = { close: close, drawer: drawer, body: body };
      activeDrawer = ctrl;
      return ctrl;
    },
    closeActive: function () { if (activeDrawer) activeDrawer.close(); }
  };

  /* --- UI.Modal -----------------------------------------------
     open({ title, contentHtml|contentNode, onClose }) → controller
     ----------------------------------------------------------- */
  UI.Modal = {
    open: function (opts) {
      opts = opts || {};
      var backdrop = document.createElement('div');
      backdrop.className = 'cfg-modal-backdrop';

      var modal = document.createElement('div');
      modal.className = 'cfg-modal';
      var inner = '';
      if (opts.title) {
        inner += '<h3 class="cfg-drawer-title">' +
          String(opts.title).replace(/</g, '&lt;') + '</h3>';
      }
      modal.innerHTML = inner;

      var body = document.createElement('div');
      if (opts.contentNode) body.appendChild(opts.contentNode);
      else if (opts.contentHtml) body.innerHTML = opts.contentHtml;
      modal.appendChild(body);

      backdrop.appendChild(modal);
      document.body.appendChild(backdrop);
      requestAnimationFrame(function () { backdrop.classList.add('is-open'); });

      function close() {
        backdrop.classList.remove('is-open');
        setTimeout(function () {
          if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
          if (typeof opts.onClose === 'function') opts.onClose();
        }, tokens.durBase);
      }
      backdrop.addEventListener('click', function (e) {
        if (e.target === backdrop) close();
      });

      return { close: close, modal: modal, body: body };
    }
  };

  /* --- UI.Toast -----------------------------------------------
     show(message, { duration }) — auto-dismiss
     ----------------------------------------------------------- */
  UI.Toast = {
    show: function (message, opts) {
      opts = opts || {};
      var duration = opts.duration || 3000;
      var el = document.createElement('div');
      el.className = 'cfg-toast';
      el.textContent = String(message);
      document.body.appendChild(el);
      requestAnimationFrame(function () { el.classList.add('is-open'); });
      setTimeout(function () {
        el.classList.remove('is-open');
        setTimeout(function () {
          if (el.parentNode) el.parentNode.removeChild(el);
        }, tokens.durBase);
      }, duration);
    }
  };

  /* ----------------------------------------------------------
     BUDGET BRIDGE  (Configurator.budget)
     Configurator publishes a BoQ payload; budget.liquid (or any
     other consumer) subscribes. Per-source localStorage namespace
     so multiple products on one page don't collide.

     payload shape:
       {
         source:  'pool-cygnus-i',        // required, kebab-case
         items:   [{ code, qty, unit?, ... }],   // required array
         meta:    { ... }                  // free-form
       }
     ---------------------------------------------------------- */
  var LS_PREFIX = 'cfg.budget.';

  function validateBudget(payload) {
    if (!payload || typeof payload !== 'object') return 'payload must be object';
    if (!payload.source || typeof payload.source !== 'string') return 'payload.source required';
    if (!Array.isArray(payload.items)) return 'payload.items must be array';
    for (var i = 0; i < payload.items.length; i++) {
      var it = payload.items[i];
      if (!it || typeof it.code !== 'string') return 'items[' + i + '].code required string';
      if (typeof it.qty !== 'number' || !isFinite(it.qty)) return 'items[' + i + '].qty required number';
    }
    return null;
  }

  var budget = {
    publish: function (payload) {
      var err = validateBudget(payload);
      if (err) { log.error('[budget] invalid payload: ' + err); return false; }
      try {
        localStorage.setItem(
          LS_PREFIX + payload.source,
          JSON.stringify({ payload: payload, ts: Date.now() })
        );
      } catch (e) {
        log.warn('[budget] localStorage failed: ' + e.message);
      }
      bus.emit('budget:publish', payload);
      return true;
    },
    subscribe: function (fn) { return bus.on('budget:publish', fn); },
    getLatest: function (source) {
      try {
        var raw = localStorage.getItem(LS_PREFIX + source);
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) { return null; }
    },
    listSources: function () {
      var out = [];
      try {
        for (var i = 0; i < localStorage.length; i++) {
          var k = localStorage.key(i);
          if (k && k.indexOf(LS_PREFIX) === 0) out.push(k.slice(LS_PREFIX.length));
        }
      } catch (e) {}
      return out;
    },
    clear: function (source) {
      try { localStorage.removeItem(LS_PREFIX + source); } catch (e) {}
    }
  };

  /* ----------------------------------------------------------
     PURCHASE GATE  (Configurator.purchaseGate)

     Restricts PDF export to customers who have purchased the
     current product. Router emits window.LEVA_PRODUCT with the
     hasPurchased flag (computed Liquid-side from customer.orders);
     we wrap ConfiguratorPdfExporter.export to enforce it.

     Gate is universal across all configurators (pool, rack, fence,
     shed, default) because all of them call the same shared
     window.ConfiguratorPdfExporter.export().
     ---------------------------------------------------------- */
  var purchaseGate = (function () {
    var modalEl = null;
    var installed = false;

    function product() { return global.LEVA_PRODUCT || {}; }
    /* Source of truth for "is the active variant purchased" must match the
       asset drawer, which gates on ConfiguratorBudgetBridge per VARIANT.
       LEVA_PRODUCT.hasPurchased is product-level, so on a multi-variant
       product (e.g. benches) it would let the PDF through while the asset
       manager correctly locks. Prefer the bridge; fall back to the
       product-level flag only when the bridge isn't on the page. */
    function isPurchased() {
      var bridge = global.ConfiguratorBudgetBridge;
      if (bridge && typeof bridge.isCurrentVariantPurchased === 'function') {
        try { return !!bridge.isCurrentVariantPurchased(); }
        catch (e) { /* fall through to product-level flag */ }
      }
      return !!product().hasPurchased;
    }

    function buildModal() {
      if (modalEl) return modalEl;
      /* Read shared translations injected by configurator-router.liquid
         (window.LEVA_I18N). Same generic strings the asset-manager lock
         uses — one message for budgeting / blueprints / PDF across all
         configurators, so locales stay small. English fallbacks keep the
         modal working if the router hasn't emitted LEVA_I18N yet. */
      var T = (global.LEVA_I18N) || {};
      var tTitle = T.purchaseRequiredTitle || T.lockedOverlayTitle
        || 'Purchase to unlock';
      var tBody  = T.purchaseRequiredBody  || T.requiresPurchase
        || 'Please purchase this variant first to access the online budgeting feature and 2D blueprints.';
      var tClose = T.close || 'Close';

      var div = document.createElement('div');
      div.id = 'cf-purchase-gate-modal';
      div.setAttribute('style',
        'position:fixed;inset:0;background:rgba(0,0,0,0.30);' +
        'z-index:99999;display:none;align-items:center;justify-content:center;' +
        '-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);' +
        'font-family:Montserrat,-apple-system,sans-serif;');
      div.innerHTML = ''
        + '<div style="background:#fff;border:1px solid #DADCE0;border-radius:16px;'
        +              'box-shadow:0 8px 24px rgba(70,53,41,0.18);padding:28px 24px;'
        +              'width:min(360px,calc(100vw - 32px));text-align:center;">'
        +   '<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#D2A878" '
        +        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '
        +        'style="margin-bottom:12px;display:inline-block;" aria-hidden="true">'
        +     '<rect x="3" y="11" width="18" height="11" rx="2"></rect>'
        +     '<path d="M7 11V7a5 5 0 0 1 10 0v4"></path>'
        +   '</svg>'
        +   '<h2 style="margin:0 0 10px;font-size:18px;font-weight:700;color:#1A1C1D;letter-spacing:-0.01em;">'
        +     escapeHtml(tTitle)
        +   '</h2>'
        +   '<p style="margin:0 0 18px;font-size:14px;line-height:1.5;font-weight:500;color:#6b6b6b;">'
        +     escapeHtml(tBody)
        +   '</p>'
        +   '<button id="cf-pg-close" type="button"'
        +        ' style="display:block;width:100%;padding:11px 14px;border:none;border-radius:25px;'
        +              'background:#D2A878;color:#fff;cursor:pointer;font:700 13px Montserrat,sans-serif;'
        +              'letter-spacing:0.02em;">'
        +     escapeHtml(tClose)
        +   '</button>'
        + '</div>';
      div.addEventListener('click', function (e) {
        if (e.target === div) closeModal();
      });
      document.body.appendChild(div);
      var closeBtn = div.querySelector('#cf-pg-close');
      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      modalEl = div;
      return div;
    }

    function escapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function showModal() {
      var el = buildModal();
      el.style.display = 'flex';
    }

    function closeModal() {
      if (modalEl) modalEl.style.display = 'none';
    }

    function installPdfGate() {
      function tryWrap() {
        var exp = global.ConfiguratorPdfExporter;
        if (!exp || typeof exp.export !== 'function') return false;
        if (exp.__leva_gated) { installed = true; return true; }
        var orig = exp.export;
        /* core-v9-credits (Marek: PDF za tokeny): a PDF paid in tokens a
           moment ago (configurator-pdf.liquid pdf-pay-v5 / the house core
           set __levaPaidAt) goes through whether the product was bought or
           not -- otherwise the tokens are taken and the PDF is refused */
        function paidRecently() { var t = exp.__levaPaidAt; return !!(t && (Date.now() - t) < 600000); }
        exp.export = function () {
          if (!isPurchased() && !paidRecently()) {
            showModal();
            if (global.Configurator && global.Configurator.log) {
              global.Configurator.log.log('[purchase-gate] PDF blocked — product not purchased');
            }
            return Promise.resolve({ blocked: true, reason: 'not-purchased' });
          }
          return orig.apply(this, arguments);
        };
        exp.__leva_gated = true;
        installed = true;
        if (global.Configurator && global.Configurator.log) {
          global.Configurator.log.log('[purchase-gate] PDF exporter wrapped (purchased=' + isPurchased() + ')');
        }
        return true;
      }
      if (installed) return;
      if (tryWrap()) return;
      /* Exporter not yet defined — retry up to 5s while libs load. */
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (tryWrap() || tries > 50) clearInterval(iv);
      }, 100);
    }

    return {
      isPurchased: isPurchased,
      product: product,
      installPdfGate: installPdfGate,
      showModal: showModal,
      closeModal: closeModal
    };
  })();

  /* ----------------------------------------------------------
     THE PDF PACKAGE ON THE DRAWINGS PAGE  (pdf-pkg-v2, core-v11-pkg)

     Marek 2026-10-27: PDF u vsech aktivnich projektu stejne (krome
     puvodniho Taurus a garden swing od Andrease). A configurator whose PDF
     goes through ConfiguratorPdfExporter.socialGate (benches, swings, garden
     structures, fences, pool, outdoor kitchen, shelters, workout, container)
     takes no tokens here any more: the gate arms the package (the same
     token key, the owner flag) and lets the purchase gate through; the rich
     export (configurator-pdf.liquid, which calls _pkgWanted / _pkgSave)
     makes the PDF as always, then puts a list of pages in as page 2,
     numbers every page after the cover, keeps the file in this browser
     (indexedDB leva-pkg, kind doc) and opens /pages/blueprints?doc=<id> --
     every page on the screen, the first five sharp, the tokens taken there
     when it is downloaded. Lives here, not in the exporter snippet, which is
     at Shopify's 256 KB limit for a Liquid file. The house line's core never
     calls socialGate (it hands its own package), a royalty product's PDF
     neither (the purchase gate) -- both unchanged; a page with
     window.LEVA_PDF_PACKAGE = false keeps the old way.
     ---------------------------------------------------------- */
  var pdfPackage = (function () {
    var installed = false;
    function word(X, k, fb) { var v = (X._T || {})[k]; return (typeof v === 'string' && v && !/translation missing/i.test(v)) ? v : fb; }
    function onPage() {
      if (global.BLUEPRINT_LIBS) return false;
      if (global.LEVA_PDF_PACKAGE === false) return false;
      return !!global.indexedDB;
    }
    function put(id, rec) {
      return new Promise(function (ok, no) {
        var rq;
        try { rq = global.indexedDB.open('leva-pkg', 1); } catch (e) { no(e); return; }
        rq.onupgradeneeded = function () { try { rq.result.createObjectStore('pkg'); } catch (eU) {} };
        rq.onerror = function () { no(rq.error || new Error('indexedDB did not open')); };
        rq.onsuccess = function () {
          var db = rq.result, tx;
          try { tx = db.transaction('pkg', 'readwrite'); } catch (eT) { db.close(); no(eT); return; }
          var st = tx.objectStore('pkg'), old = Date.now() - 30 * 86400000;
          st.put(rec, id);
          try { var cr = st.openCursor(); cr.onsuccess = function () { var c = cr.result; if (!c) return; if (c.value && c.value.at < old) c['delete'](); c['continue'](); }; } catch (eC) {}
          tx.oncomplete = function () { db.close(); ok(); };
          tx.onerror = tx.onabort = function () { db.close(); no(tx.error || new Error('indexedDB write failed')); };
        };
      });
    }
    /* one row per kind of page (custom pages one per title), its own heading,
       pages counted after the list is in */
    function rows(X, secs) {
      var T = X._T || {};
      var T2 = { boq: T.pageBoqTitle, shopping: T.pageShoppingTitle, userView: T.pageIsoTitle, userSnap: T.pageIsoTitle,
                 view3d: T.pageIsoTitle, isometric: T.pageIsoTitle, view2d: T.pageOrthosTitle, orthos: T.pageOrthosTitle,
                 snapshotOrthos: T.pageOrthosTitle, basePlan: T.pkgPlan, baseCrossSection: T.pageOrthosTitle, baseAssembly: T.pageIsoTitle };
      var out = [], last = null;
      (secs || []).forEach(function (q) {
        if (q.kind === 'cover') return;
        var pre = String(q.kind).split(':')[0], key = (pre === 'custom') ? ('custom|' + (q.title || q.kind)) : pre;
        if (last && last.key === key) { last.to = q.to + 1; return; }
        last = { key: key, label: (pre === 'cutListVisual' ? (T.pageCutListTitle || q.title) : (q.title || T2[pre])) || pre, from: q.from + 1, to: q.to + 1 };
        out.push(last);
      });
      return out;
    }
    function contents(X, doc, args, ctx, list) {
      var W = X.L_PAGE_W, M = X.L_MARGIN;
      ctx.pageNum = 2;
      X._drawRichPageHeader(doc, word(X, 'pkgIndex', 'Sheet index'), ctx);
      var all = [{ label: word(X, 'pkgProject', 'Project'), from: 1, to: 1 }, { label: word(X, 'pkgIndex', 'Sheet index'), from: 2, to: 2 }].concat(list);
      var step = Math.min(9, (X.L_PAGE_H - 68) / Math.max(1, all.length)), y = 34;
      /* core-v13-look (Marek 2026-10-28: design C na cele PDF): a table
         without cell borders -- warm zebra rows, the pages in the LEVA brown */
      all.forEach(function (r, i) {
        if (i % 2 === 0) { X._setFill(doc, X.COLORS.paperAlt); doc.rect(M - 2, y - step + 2.6, W - 2 * M + 4, step, 'F'); }
        doc.setFont(ctx.fontFamily, 'bold'); doc.setFontSize(11);
        X._setText(doc, X.COLORS.text);
        doc.text(String(r.label || ''), M, y);
        X._setText(doc, X.COLORS.brown || X.COLORS.text);
        doc.text(r.from === r.to ? String(r.from) : (r.from + ' - ' + r.to), W - M - 2, y, { align: 'right' });
        y += step;
      });
      try { X._drawRichBrandBar(doc, args, ctx); } catch (e) {}
    }
    function save(X, doc, secs, args, ctx) {
      var t0 = Date.now(), list = rows(X, secs);
      doc.insertPage(2);
      doc.setPage(2);
      var N = doc.getNumberOfPages(), i;
      contents(X, doc, args, ctx, list);
      for (i = 2; i <= N; i++) {
        doc.setPage(i);
        doc.setFont(ctx.fontFamily, 'bold'); doc.setFontSize(8); doc.setTextColor(140, 122, 102);
        doc.text(i + ' / ' + N, X.L_PAGE_W - X.L_MARGIN, 10, { align: 'right' });
      }
      var title = String(args.productTitle || '').split('\n')[0];
      var handle = (args.meta && args.meta.productHandle) || '';
      if (!handle) { try { var mH = /\/products\/([^\/?#]+)/.exec(global.location.pathname); handle = mH ? mH[1] : ''; } catch (eH) {} }
      var id = 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      var ts = new Date().toISOString().slice(0, 10);
      var rec = { v: 3, kind: 'doc', at: Date.now(), id: id, key: X._pkgKeyHash || '', owner: !!X._pkgOwner, title: title, handle: handle,
                  fname: (X._slugify(handle || title) || 'configuration') + '-' + ts + '.pdf', pages: N,
                  rows: [{ label: word(X, 'pkgProject', 'Project'), from: 1, to: 1 }].concat(list), bytes: doc.output('arraybuffer'), version: 'pdf-pkg-v2 / ' + VERSION,
                  buy: X._pkgBuy || null };
      return put(id, rec).then(function () {
        var root = X._payRoot(); if (root.charAt(root.length - 1) !== '/') root += '/';
        /* core-v14-bppkg: where the document is made again (src) -- the
           drawings page, opened in a browser that does not have it, sends the
           visitor there (?bppkg=1) and gets it back made */
        var url = root + 'pages/blueprints?doc=' + id + '&src=' + encodeURIComponent(srcOf(args)), w = null;
        if (asked()) { X._payLog('package ' + id + ' made for the drawings page -- back to it (core-v14-bppkg)'); global.location.replace(url + '&bpback=1'); return { package: id, url: url, note: 'back to the drawings page' }; }
        try { w = global.open(url, '_blank'); } catch (eO) { w = null; }
        var note = 'package ' + id + ': ' + N + ' page(s), ' + Math.round(rec.bytes.byteLength / 1024) + ' kB, ' + (Date.now() - t0) + ' ms, ' + (w ? 'the drawings page opened' : 'the window was held back -- the card with the link');
        X._payLog(note);
        if (!w) X._payCard(title, { label: word(X, 'pkgOpen', 'Download PDF documentation'), href: url, newTab: true });
        return { package: id, url: url, note: note };
      });
    }
    /* the address that makes this document again: the configurator's own
       link when it has one on this shop (args.shareUrl), else this page */
    function srcOf(args) {
      var drop = function (u) { return String(u).replace(/([?&])(bppkg|bpback|bpdoc)=[^&#]*&?/g, '$1').replace(/[?&](#|$)/, '$1'); };
      try {
        var u = new URL(String((args && args.shareUrl) || ''), global.location.href);
        if (u.origin === global.location.origin && /\/products\//.test(u.pathname)) return drop(u.pathname + u.search + u.hash);
      } catch (e) {}
      return drop(global.location.pathname + global.location.search + global.location.hash);
    }
    function asked() { try { return new URL(global.location.href).searchParams.get('bppkg') === '1'; } catch (e) { return false; } }
    /* THE DRAWINGS PAGE ASKS FOR THE DOCUMENT (core-v14-bppkg, blueprints
       thread, Marek 2026-10-29: chci vizualizaci kompletniho pdf na
       blueprints ... takto u vsech produktu - i Taurusy, firewood racks,
       benches). A ?doc= link opened in a browser that never made the
       document comes here with ?bppkg=1: the page covers itself ("Making
       the PDF..."), waits for the exporter, its package hook, the PDF
       button and the budget, taps the PDF button once (twice if nothing came
       in 25 s), and save() sends the visitor back to the drawings page with
       the new document (&bpback=1). The house line's own core does this for
       its package (core-v767); nothing here runs there. alert() is quiet
       meanwhile -- a configurator's "not ready yet" would stop the page. */
    function cover(X) {
      try {
        var d = global.document, o = d.getElementById('cf-bp-cover');
        if (!d.body) return;
        if (!o) {
          o = d.createElement('div'); o.id = 'cf-bp-cover';
          o.style.cssText = 'position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;background:#f5f5f5;font:700 18px Montserrat,sans-serif;color:#1A1C1D;text-align:center;padding:20px;';
          d.body.appendChild(o);
        }
        o.textContent = word(X || {}, 'pkgMaking', 'Making the PDF...');
      } catch (e) {}
    }
    function run() {
      if (!asked()) return;
      var t0 = Date.now(), taps = 0, tapAt = 0, tReady = 0, gone = false;
      var lg = function (m) { try { if (global.Configurator && global.Configurator.log) global.Configurator.log.log('[pdf-pkg] ' + m); } catch (e) {} };
      try { global.alert = function (m) { lg('alert held back while the document is made: ' + String(m).slice(0, 160)); }; } catch (eA) {}
      var back = function (why) {
        if (gone) return; gone = true;
        lg(why + ' -- back to the drawings page without a document (core-v14-bppkg)');
        try {
          var u = new URL(global.location.href), root = '/';
          try { root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; } catch (eR) {}
          if (root.charAt(root.length - 1) !== '/') root += '/';
          u.searchParams.delete('bppkg');
          global.location.replace(root + 'pages/blueprints?doc=' + encodeURIComponent(u.searchParams.get('bpdoc') || 'd0none0') + '&bpback=1');
        } catch (eB) {}
      };
      (function tick() {
        if (gone) return;
        if (global.HOUSE_BOOT || global.HouseLib) return;   /* the house line's core answers */
        var X = global.ConfiguratorPdfExporter, now = Date.now();
        cover(X);
        if (now - t0 > 90000) { back('nothing made in 90 s'); return; }
        var btn = global.document.getElementById('cf-btn-pdf'), br = global.ConfiguratorBudgetBridge, ready = true;
        try { if (br && typeof br.isReady === 'function') ready = !!br.isReady(); } catch (eR) { ready = true; }
        if (!(X && X.__levaPkg && btn) || (!ready && now - t0 < 45000)) { global.setTimeout(tick, 700); return; }
        if (!tReady) tReady = now;
        if (now - tReady < 2500) { global.setTimeout(tick, 700); return; }
        if (!taps || (taps < 2 && now - tapAt > 25000)) {
          taps++; tapAt = now;
          lg('the PDF button tapped for the drawings page (' + taps + ')');
          try { btn.click(); } catch (eC) { lg('the tap THREW ' + ((eC && eC.message) || eC)); }
        }
        global.setTimeout(tick, 1000);
      })();
    }
    function install() {
      run();
      function tryInstall() {
        var X = global.ConfiguratorPdfExporter;
        if (!X || typeof X.socialGate !== 'function') return false;
        if (X.__levaPkg) { installed = true; return true; }
        var gate = X.socialGate;
        X.socialGate = function (opts) {
          opts = opts || {};
          if (!onPage()) return gate.apply(this, arguments);
          var LP = global.LEVA_PRODUCT || {};
          X._pkgArmed = Date.now();
          X._pkgKeyHash = opts.key ? X._payKey(opts.key) : '';
          X._pkgOwner = !!opts.paid || LP.paidOwner === true || LP.paidOwner === 'true';
          /* core-v12-buy (rack-v10-docs): a royalty product (the firewood
             rack) is paid by its purchase, not tokens -- the drawings page
             offers the purchase instead of the price ({ text, btn, url }) */
          X._pkgBuy = (!X._pkgOwner && opts.buy && opts.buy.url) ? opts.buy : null;
          X.__levaPaidAt = Date.now();
          X._payLog('package -- no tokens here, the drawings page shows the PDF and takes them on download (pdf-pkg-v2)' + (X._pkgOwner ? ', owner' : ''));
          return Promise.resolve();
        };
        X._pkgPage = onPage;
        X._pkgWanted = function (args) { return !!(X._pkgArmed && Date.now() - X._pkgArmed < 300000 && !(args && (args.__returnDoc || args.prependPdf)) && onPage()); };
        X._pkgSave = function (doc, secs, args, ctx) { X._pkgArmed = 0; return save(X, doc, secs, args, ctx); };
        X.__levaPkg = true;
        installed = true;
        if (global.Configurator && global.Configurator.log) global.Configurator.log.log('[pdf-pkg] the PDF opens on the drawings page (pdf-pkg-v2, ' + VERSION + ')' + (onPage() ? '' : ' -- off on this page'));
        return true;
      }
      if (installed || tryInstall()) return;
      var tries = 0;
      var iv = setInterval(function () { tries++; if (tryInstall() || tries > 50) clearInterval(iv); }, 100);
    }
    return { install: install };
  })();

  /* ----------------------------------------------------------
     PAYWALL  (Configurator.paywall)

     Visually obscures numeric values in the bottom info bar for
     users who haven't purchased the current project. Structural
     labels (what is being measured) stay readable so the user can
     see WHAT data is here; only the numbers are blurred.

     Reuses purchaseGate.isPurchased() so detection logic is shared
     with the PDF gate — one source of truth for the purchase state.

     The CSS is injected once and reacts to .cfg-paywalled on <body>.
     paywall.refresh() toggles the class — call it after the LEVA_PRODUCT
     window field is populated (boot does this) or any time purchase
     state may have changed (e.g. after an in-page purchase event).

     Targets .cf-info-value by design: a class that's already used by
     every configurator's bottom info bar (rack, pool, default, fence),
     so a single CSS rule paywalls all of them without per-config wiring.
     ---------------------------------------------------------- */
  var paywall = (function () {
    var STYLE_ID = 'cfg-paywall-styles';

    function injectCSS() {
      if (document.getElementById(STYLE_ID)) return;
      var css = [
        '/* Configurator paywall — numeric values in the BOTTOM INFO BAR',
        '   appear bleached toward white when body has .cfg-paywalled.',
        '   Labels and units stay clear so the user sees WHAT data is',
        '   there, just not the figures.',
        '   ',
        '   Scope: only .cf-info-value (the BoQ totals at the bottom).',
        '   The budget widget (Total / Material / Execution) is NOT',
        '   paywalled — it works normally with all values visible.',
        '   ',
        '   Effect: heavy blur softens the shape almost beyond reading,',
        '   low opacity (0.2) bleeds the (dark) text into the light',
        '   page background — the figures fade to near-white rather',
        '   than leaving a dark smear. */',
        '.cfg-paywalled .cf-info-value {',
        '  filter: blur(6px);',
        '  -webkit-filter: blur(6px);',
        '  opacity: 0.2;',
        '  user-select: none;',
        '  -webkit-user-select: none;',
        '  transition: filter 0.2s ease, opacity 0.2s ease;',
        '}'
      ].join('\n');
      var style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = css;
      (document.head || document.documentElement).appendChild(style);
    }

    function applyClass(locked) {
      if (document.body) {
        document.body.classList.toggle('cfg-paywalled', locked);
      } else {
        /* Body not yet present — defer until DOM is ready. */
        document.addEventListener('DOMContentLoaded', function () {
          document.body.classList.toggle('cfg-paywalled', locked);
        }, { once: true });
      }
    }

    function refresh() {
      injectCSS();
      var locked = !purchaseGate.isPurchased();
      applyClass(locked);
      return locked;
    }

    function isLocked() { return !purchaseGate.isPurchased(); }

    return { refresh: refresh, isLocked: isLocked };
  })();

  /* ----------------------------------------------------------
     PERSON  (Configurator.person)
     Shared 175 cm scale-reference figure for ALL configurators
     (rack, sofa/bench, fence, foundations, future projects).
     Each configurator registers once with init({...}) supplying:
       • getEngine() → { benchGroup|group, scene, camera, renderer }
       • getState()  → object with { bodyVisible, bodyX, bodyZ }
       • frontOf()   → { x, z } default placement in front of product
       • visibleAt(stage,view) → bool   (optional; default: always)
     Then the configurator calls:
       • Configurator.person.attach()   after every rebuild (re-spawns)
       • Configurator.person.toggle()   from the body button
     The GLB is loaded + cached ONCE here and reused across configurators.
     ---------------------------------------------------------- */
  var person = (function () {
    var PERSON_GLB_URL = 'https://cdn.shopify.com/3d/models/a185bdffeca46290/human.glb';
    var _scene = null;        /* cached, fitted, faded GLB scene (template) */
    var _loading = null;      /* in-flight load promise */
    var _cfg = null;          /* the registered configurator hooks */

    function _plog(msg) {
      if (log && log.log) log.log('[person] ' + msg);
    }

    /* Resolve a THREE.GLTFLoader across r128..r160 (classic JS then ESM). */
    function _getLoader() {
      return new Promise(function (resolve, reject) {
        var T = global.THREE;
        if (T && T.GLTFLoader) { resolve(T.GLTFLoader); return; }
        if (global.GLTFLoader) { resolve(global.GLTFLoader); return; }
        if (!T) { reject(new Error('THREE not on window')); return; }
        var rev = T.REVISION || '128';
        var script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/three@0.' + rev + '.0/examples/js/loaders/GLTFLoader.js';
        script.onload = function () {
          if (T.GLTFLoader) { resolve(T.GLTFLoader); return; }
          if (global.GLTFLoader) { resolve(global.GLTFLoader); return; }
          _tryESM();
        };
        script.onerror = _tryESM;
        document.head.appendChild(script);
        function _tryESM() {
          var url = 'https://esm.sh/three@0.' + rev + '/examples/jsm/loaders/GLTFLoader.js';
          try {
            import(url).then(function (mod) {
              if (mod && mod.GLTFLoader) resolve(mod.GLTFLoader);
              else reject(new Error('no GLTFLoader in esm.sh module'));
            }).catch(reject);
          } catch (e) { reject(e); }
        }
      });
    }

    /* Load + cache the GLB scene template (fit to 175 cm, feet at Y=0,
       centred on X/Z, faded near-white material). */
    function _load() {
      if (_scene) return Promise.resolve(_scene);
      if (_loading) return _loading;
      _loading = _getLoader().then(function (Loader) {
        return new Promise(function (resolve, reject) {
          var T = global.THREE;
          var loader = new Loader();
          loader.load(PERSON_GLB_URL, function (gltf) {
            var scene = gltf.scene;
            var b1 = new T.Box3().setFromObject(scene);
            var s1 = b1.getSize(new T.Vector3());
            if (s1.y > 0) scene.scale.setScalar(1.75 / s1.y);
            var b2 = new T.Box3().setFromObject(scene);
            var c2 = b2.getCenter(new T.Vector3());
            scene.position.set(-c2.x, -b2.min.y, -c2.z);
            var mat = new T.MeshStandardMaterial({
              color: 0x8c9199, transparent: true, opacity: 0.28,   /* v10: lighter grey, 0.40 -> 0.28 (Marek: postava moc tmava) */
              depthWrite: false, roughness: 1.0,
              emissive: 0x000000, emissiveIntensity: 0
            });
            scene.traverse(function (ch) {
              if (ch.isMesh) { ch.material = mat; ch.castShadow = false; ch.receiveShadow = false; }
            });
            _scene = scene;
            _plog('GLB loaded + cached');
            resolve(scene);
          }, null, function (err) {
            _plog('GLB load failed: ' + (err && err.message ? err.message : err));
            reject(err);
          });
        });
      });
      return _loading;
    }

    function _engine() { return _cfg && _cfg.getEngine && _cfg.getEngine(); }
    function _group()  { var e = _engine(); return e && (e.benchGroup || e.group || e.modelGroup); }
    function _state()  { return (_cfg && _cfg.getState && _cfg.getState()) || {}; }

    function _render() {
      var e = _engine();
      if (e && e.renderer && e.scene && e.camera) e.renderer.render(e.scene, e.camera);
    }

    /* Register a configurator's hooks. Called once at boot per lib. */
    function init(cfg) {
      _cfg = cfg || {};
      _plog('init by ' + (_cfg.name || 'configurator'));
      return person;
    }

    /* (Re)create the person anchor in the current model group. Called
       after every rebuild because the group is wiped on rebuild. The
       anchor is always created (so assembly tagging can position it);
       visibility is decided by visibleAt()/bodyVisible elsewhere. */
    function attach() {
      var T = global.THREE;
      var grp = _group();
      if (!T || !grp || !_cfg) return;
      var st = _state();
      var front = (_cfg.frontOf && _cfg.frontOf()) || { x: 0.6, z: 0 };
      var bx = (st.bodyX != null) ? st.bodyX : front.x;
      var bz = (st.bodyZ != null) ? st.bodyZ : front.z;
      var anchor = new T.Group();
      anchor.position.set(bx, 0, bz);
      anchor.userData.partType = 'person';
      anchor.userData.basePos = anchor.position.clone();
      grp.add(anchor);
      if (_scene) {
        anchor.add(_scene);
      } else {
        _load().then(function (scene) {
          var g = _group();
          if (!g) return;
          for (var i = 0; i < g.children.length; i++) {
            var c = g.children[i];
            if (c.userData && c.userData.partType === 'person' && c.children.length === 0) {
              c.add(scene); break;
            }
          }
          _render();
        }).catch(function () {});
      }
      return anchor;
    }

    /* Toggle visibility flag on the configurator's state + rebuild so the
       visibility rules re-apply. Returns the new bodyVisible value. */
    function toggle() {
      var st = _state();
      st.bodyVisible = !st.bodyVisible;
      if (_cfg && _cfg.onToggle) _cfg.onToggle(st.bodyVisible);
      return st.bodyVisible;
    }

    function isVisible() { return _state().bodyVisible !== false; }

    /* Whether the figure may show at a given stage/view (lib rule, else
       always true). The lib still owns the bodyVisible flag. */
    function visibleAt(stage, view) {
      if (_cfg && _cfg.visibleAt) return _cfg.visibleAt(stage, view);
      return true;
    }

    function setPos(x, z) {
      var st = _state();
      if (typeof x === 'number') st.bodyX = x;
      if (typeof z === 'number') st.bodyZ = z;
    }

    /* Eagerly warm the GLB cache (optional). */
    function preload() { return _load(); }

    return {
      init: init, attach: attach, toggle: toggle, isVisible: isVisible,
      visibleAt: visibleAt, setPos: setPos, preload: preload,
      GLB_URL: PERSON_GLB_URL
    };
  })();

  /* ----------------------------------------------------------
     PUBLIC EXPORT
     Merge into existing global.Configurator if it exists (e.g.
     configurator-debug.js may have loaded first and populated
     .debug). Never overwrite the whole object.
     ---------------------------------------------------------- */
  var existing = global.Configurator || {};
  global.Configurator = existing;
  existing.version       = VERSION;
  existing.tokens        = tokens;
  existing.units         = units;
  existing.bus           = bus;
  existing.log           = log;
  existing.icon          = icon;
  existing.registerIcon  = registerIcon;
  existing.UI            = UI;
  existing.budget        = budget;
  existing.purchaseGate  = purchaseGate;
  existing.paywall       = paywall;
  existing.person        = person;
  existing.__sealed      = true;

  /* ============================================================
     COLLAPSIBLE BUDGET WIDGET (centralized)
     Runtime DOM normalisation for #cf-budget-widget. This runs
     regardless of what the Liquid template generated, so a stale
     Shopify CDN cache serving the legacy HTML (close button + all
     rows expanded) is fixed on the client side.

     Behaviour after init:
       • Removes any .cf-budget-widget__close button from the DOM
       • Ensures a .cf-budget-widget__toggle (chevron) exists in the
         top-right; creates it if the template didn't include one
       • Default state COLLAPSED — breakdown + capacity hidden via
         inline style.display='none'
       • Tap toggle → flip data-expanded; chevron rotates via CSS
       • Same behaviour on desktop and mobile (no media query)

     Used by all configurators (rack, pool, fence, ...) because
     core.js is loaded by every configurator template via
     sections/configurator-router.liquid.
     ============================================================ */
  var widgetCollapsible = (function () {
    var INITIALISED_ATTR = 'data-cf-collapsible-ready';

    function init() {
      var widget = document.getElementById('cf-budget-widget');
      if (!widget) return false;
      if (widget.getAttribute(INITIALISED_ATTR) === 'true') return true;

      /* 1. Remove legacy close button (if rendered by a stale cache). */
      var closeBtn = widget.querySelector('.cf-budget-widget__close');
      if (closeBtn && closeBtn.parentNode) {
        closeBtn.parentNode.removeChild(closeBtn);
      }

      /* 2. Ensure toggle button exists. */
      var toggleBtn = widget.querySelector('.cf-budget-widget__toggle');
      if (!toggleBtn) {
        toggleBtn = document.createElement('button');
        toggleBtn.className = 'cf-budget-widget__toggle';
        toggleBtn.id        = 'cf-budget-widget-toggle';
        toggleBtn.type      = 'button';
        toggleBtn.setAttribute('aria-label',    'Toggle details');
        toggleBtn.setAttribute('aria-expanded', 'false');
        toggleBtn.innerHTML  =
          '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path d="M4 6 L8 10 L12 6" stroke="currentColor" stroke-width="1.8" ' +
                'stroke-linecap="round" stroke-linejoin="round"/></svg>';
        /* Apply inline styling so it works even if the stylesheet is
           missing or has been overridden by older themes. */
        toggleBtn.style.cssText = [
          'position:absolute',
          'top:6px', 'right:8px',
          'background:transparent',
          'border:none',
          'color:#6b7280',
          'cursor:pointer',
          'padding:4px',
          'line-height:1',
          'z-index:2',
          'display:block'
        ].join(';');
        widget.appendChild(toggleBtn);

        /* Style its SVG (size + rotation transition). */
        var svgEl = toggleBtn.querySelector('svg');
        if (svgEl) {
          svgEl.style.cssText = 'width:14px;height:14px;display:block;transition:transform 0.2s ease';
        }
      }

      /* 3. Hide breakdown + capacity until expanded. Inline styles win
            over any cached stylesheet. */
      var breakdown = widget.querySelector('.cf-budget-widget__breakdown');
      var capacity  = widget.querySelector('.cf-budget-widget__capacity');
      if (breakdown) breakdown.style.display = 'none';
      if (capacity)  capacity.style.display  = 'none';

      /* 4. Make the widget content-sized when collapsed (so it
            doesn't dominate the canvas). When expanded we widen
            back to the desktop footprint. */
      function applyWidth(open) {
        if (open) {
          widget.style.width = '240px';
        } else {
          widget.style.width    = 'auto';
          widget.style.minWidth = '160px';
          widget.style.maxWidth = 'calc(100vw - 32px)';
        }
      }
      applyWidth(false);

      /* 5. Tighter padding + left-align for the total row when
            collapsed (gives the toggle room in the corner). */
      var totalRow = widget.querySelector('.cf-budget-widget__total');
      var totalVal = widget.querySelector('.cf-budget-widget__total-value');
      function applyTotalStyle(open) {
        if (!totalRow) return;
        if (open) {
          totalRow.style.padding      = '14px 30px 12px';
          totalRow.style.textAlign    = 'center';
          totalRow.style.borderBottom = '1px solid rgba(0,0,0,0.06)';
          if (totalVal) totalVal.style.fontSize = '26px';
        } else {
          totalRow.style.padding      = '10px 32px 10px 14px';
          totalRow.style.textAlign    = 'left';
          totalRow.style.borderBottom = 'none';
          if (totalVal) totalVal.style.fontSize = '22px';
        }
      }
      applyTotalStyle(false);

      /* 6. Toggle handler — pure DOM manipulation, no CSS dependency. */
      toggleBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = widget.getAttribute('data-expanded') === 'true';
        if (open) {
          widget.removeAttribute('data-expanded');
          toggleBtn.setAttribute('aria-expanded', 'false');
          if (breakdown) breakdown.style.display = 'none';
          if (capacity)  capacity.style.display  = 'none';
          var svg1 = toggleBtn.querySelector('svg');
          if (svg1) svg1.style.transform = '';
          applyWidth(false);
          applyTotalStyle(false);
        } else {
          widget.setAttribute('data-expanded', 'true');
          toggleBtn.setAttribute('aria-expanded', 'true');
          if (breakdown) breakdown.style.display = 'grid';
          if (capacity)  capacity.style.display  = 'block';
          var svg2 = toggleBtn.querySelector('svg');
          if (svg2) svg2.style.transform = 'rotate(180deg)';
          applyWidth(true);
          applyTotalStyle(true);
        }
      });

      widget.setAttribute(INITIALISED_ATTR, 'true');
      return true;
    }

    /* Try to initialise immediately; if the widget isn't in the DOM
       yet (loaded by a later snippet), retry until it appears or we
       give up after ~10 seconds. */
    function tryInit() {
      if (init()) return;
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (init() || tries > 100) clearInterval(iv);
      }, 100);
    }

    return { init: init, tryInit: tryInit };
  })();

  existing.widgetCollapsible = widgetCollapsible;

  /* ----------------------------------------------------------
     BOOT
     Register core status, flush any queued status providers
     once Configurator.debug is present. No UI is mounted here.
     ---------------------------------------------------------- */
  function boot() {
    log.registerStatus('Configurator core',
      function () { return true; },
      function () { return VERSION; });
    log.registerStatus('Purchase gate',
      function () { return purchaseGate.installed === true || !!(global.ConfiguratorPdfExporter && global.ConfiguratorPdfExporter.__leva_gated); },
      function () { return purchaseGate.isPurchased() ? 'unlocked' : 'locked'; });
    log.registerStatus('Paywall',
      function () { return !!document.getElementById('cfg-paywall-styles'); },
      function () { return paywall.isLocked() ? 'LOCKED' : 'UNLOCKED'; });
    purchaseGate.installPdfGate();
    pdfPackage.install();
    paywall.refresh();
    widgetCollapsible.tryInit();
    flushPending();

    /* Re-evaluate the paywall whenever the active variant changes. The
       configurator fires 'configurator:variant-changed' on an internal
       switch; the budget bridge fires 'configurator:user-prices-updated'
       when the active variant (and thus purchase state) changes from any
       source. Without re-running refresh() here the .cfg-paywalled class
       was frozen at boot — so switching variants kept the first variant's
       locked/unlocked masking. */
    global.addEventListener('configurator:variant-changed', function () {
      paywall.refresh();
    });
    global.addEventListener('configurator:user-prices-updated', function () {
      paywall.refresh();
    });
    /* If debug script loads later than core, retry briefly. */
    if (!dbg()) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (dbg()) { flushPending(); clearInterval(iv); }
        else if (tries > 50) { clearInterval(iv); }
      }, 100);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})(window);